/**
 * LinkConversionInsight — shows when the dealer's slug link has attracted
 * clicks but no shopper has completed the RV Match yet. Points at the most
 * likely reasons and offers direct actions to unblock the funnel.
 *
 * Deliberately silent (renders nothing) when the condition doesn't apply so
 * the parent can drop this in unconditionally without cluttering the layout.
 */
import React from 'react';
import { Compass, ExternalLink, ClipboardList, Copy, Share2 } from 'lucide-react';

const LinkConversionInsight = ({ clickCount = 0, realLeadCount = 0, isDark, onOpenPublicPage, onGoTo, onCopyLink }) => {
  if (clickCount <= 0 || realLeadCount > 0) return null;

  return (
    <div
      className={`rounded-2xl border p-5 sm:p-6 relative overflow-hidden ${
        isDark
          ? 'bg-gradient-to-br from-cyan-950/40 via-slate-900/60 to-indigo-950/30 border-cyan-500/25'
          : 'bg-gradient-to-br from-cyan-50 via-white to-indigo-50 border-cyan-200'
      }`}
      data-testid="link-conversion-insight"
    >
      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

      <div className="relative flex items-start gap-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
          isDark ? 'bg-cyan-400/15 text-cyan-300' : 'bg-cyan-100 text-cyan-700'
        }`}>
          <Compass size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`text-base sm:text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {`Your RV Match link received ${clickCount} visit${clickCount === 1 ? '' : 's'}, but no shoppers have completed the match yet.`}
          </h3>
          <p className={`text-sm mt-1 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
            Something is stopping visitors from finishing the form. A few quick things to try:
          </p>
          <ul className={`text-sm mt-2 space-y-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
            <li>· Fill out the public form yourself and see what the experience looks like end-to-end.</li>
            <li>· Confirm your advisor branding (name + business card) is showing.</li>
            <li>· Review the intake form — long or unclear questions cause drop-off.</li>
            <li>· Share the link with a specific call-to-action, not just the URL alone.</li>
          </ul>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onOpenPublicPage}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md transition-colors ${
                isDark ? 'bg-cyan-400 text-black hover:bg-cyan-300' : 'bg-cyan-600 text-white hover:bg-cyan-700'
              }`}
              data-testid="insight-test-quiz"
            >
              <ExternalLink size={12} /> Test My Form
            </button>
            <button
              type="button"
              onClick={() => onGoTo?.('intake-form')}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md border transition-colors ${
                isDark ? 'border-white/15 text-white hover:bg-white/10' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
              }`}
              data-testid="insight-review-intake"
            >
              <ClipboardList size={12} /> Review Intake Form
            </button>
            <button
              type="button"
              onClick={onCopyLink}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md border transition-colors ${
                isDark ? 'border-white/15 text-white hover:bg-white/10' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
              }`}
              data-testid="insight-copy-link"
            >
              <Copy size={12} /> Copy Link Again
            </button>
            <button
              type="button"
              onClick={() => onGoTo?.('marketing')}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md border transition-colors ${
                isDark ? 'border-white/15 text-white hover:bg-white/10' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
              }`}
              data-testid="insight-open-marketing"
            >
              <Share2 size={12} /> Marketing Kit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkConversionInsight;
